import { toast } from "sonner";

// Shopify API Configuration
const SHOPIFY_API_VERSION = '2025-07';
const SHOPIFY_STORE_PERMANENT_DOMAIN = 'ubevxv-er.myshopify.com';
const SHOPIFY_STOREFRONT_URL = `https://${SHOPIFY_STORE_PERMANENT_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`;
const SHOPIFY_STOREFRONT_TOKEN = 'cd28d5106626e39f4f4c7624dac7d315';

// TypeScript Interfaces
export interface ShopifyProduct {
  node: {
    id: string;
    title: string;
    description: string;
    handle: string;
    vendor: string;
    productType: string;
    tags: string[];
    createdAt?: string;
    priceRange: {
      minVariantPrice: {
        amount: string;
        currencyCode: string;
      };
    };
    compareAtPriceRange: {
      minVariantPrice: {
        amount: string;
        currencyCode: string;
      };
    };
    seo: {
      title: string | null;
      description: string | null;
    };
    images: {
      edges: Array<{
        node: {
          url: string;
          altText: string | null;
        };
      }>;
    };
    variants: {
      edges: Array<{
        node: {
          id: string;
          title: string;
          sku: string | null;
          weight: number | null;
          weightUnit: string | null;
          price: {
            amount: string;
            currencyCode: string;
          };
          compareAtPrice: {
            amount: string;
            currencyCode: string;
          } | null;
          availableForSale: boolean;
          selectedOptions: Array<{
            name: string;
            value: string;
          }>;
        };
      }>;
    };
    options: Array<{
      name: string;
      values: string[];
    }>;
  };
}

// Storefront API helper function
export async function storefrontApiRequest(query: string, variables: Record<string, unknown> = {}) {
  const response = await fetch(SHOPIFY_STOREFRONT_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_TOKEN
    },
    body: JSON.stringify({ query, variables }),
  });

  if (response.status === 402) {
    toast.error("Shopify: Payment required", {
      description: "Shopify API access requires an active Shopify billing plan. Visit admin.shopify.com to upgrade.",
    });
    return null;
  }

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();

  if (data.errors) {
    throw new Error(`Error calling Shopify: ${data.errors.map((e: { message: string }) => e.message).join(', ')}`);
  }

  return data;
}

// GraphQL Queries
export const PRODUCTS_QUERY = `
  query GetProducts($first: Int!, $query: String) {
    products(first: $first, query: $query) {
      edges {
        node {
          id
          title
          description
          handle
          vendor
          productType
          tags
          createdAt
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          compareAtPriceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          seo {
            title
            description
          }
          images(first: 5) {
            edges {
              node {
                url
                altText
              }
            }
          }
          variants(first: 10) {
            edges {
              node {
                id
                title
                sku
                weight
                weightUnit
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
                availableForSale
                selectedOptions {
                  name
                  value
                }
              }
            }
          }
          options {
            name
            values
          }
        }
      }
    }
  }
`;

// Query for newest products sorted by creation date
export const NEWEST_PRODUCTS_QUERY = `
  query GetNewestProducts($first: Int!) {
    products(first: $first, sortKey: CREATED_AT, reverse: true) {
      edges {
        node {
          id
          title
          description
          handle
          vendor
          productType
          tags
          createdAt
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          compareAtPriceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          images(first: 5) {
            edges {
              node {
                url
                altText
              }
            }
          }
          variants(first: 10) {
            edges {
              node {
                id
                title
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
                availableForSale
                selectedOptions {
                  name
                  value
                }
              }
            }
          }
          options {
            name
            values
          }
        }
      }
    }
  }
`;

export const PRODUCT_BY_HANDLE_QUERY = `
  query GetProductByHandle($handle: String!) {
    productByHandle(handle: $handle) {
      id
      title
      description
      handle
      vendor
      productType
      tags
      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
      }
      compareAtPriceRange {
        minVariantPrice {
          amount
          currencyCode
        }
      }
      seo {
        title
        description
      }
      images(first: 10) {
        edges {
          node {
            url
            altText
          }
        }
      }
      variants(first: 50) {
        edges {
          node {
            id
            title
            sku
            weight
            weightUnit
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            availableForSale
            selectedOptions {
              name
              value
            }
          }
        }
      }
      options {
        name
        values
      }
    }
  }
`;

// Cart GraphQL Mutations
export const CART_QUERY = `
  query cart($id: ID!) {
    cart(id: $id) { id totalQuantity checkoutUrl }
  }
`;

export const CART_CREATE_MUTATION = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        lines(first: 100) { edges { node { id merchandise { ... on ProductVariant { id } } } } }
      }
      userErrors { field message }
    }
  }
`;

export const CART_LINES_ADD_MUTATION = `
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        id
        lines(first: 100) { edges { node { id merchandise { ... on ProductVariant { id } } } } }
      }
      userErrors { field message }
    }
  }
`;

export const CART_LINES_UPDATE_MUTATION = `
  mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart { id }
      userErrors { field message }
    }
  }
`;

export const CART_LINES_REMOVE_MUTATION = `
  mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart { id }
      userErrors { field message }
    }
  }
`;

// Helper functions
export function formatCheckoutUrl(checkoutUrl: string): string {
  try {
    // Shopify may return a checkoutUrl on the store's primary domain.
    // If that domain points to this Lovable site (not Shopify), it will 404.
    // Force the checkout URL to always use the permanent myshopify.com domain.

    const url = checkoutUrl.startsWith('http')
      ? new URL(checkoutUrl)
      : new URL(checkoutUrl, `https://${SHOPIFY_STORE_PERMANENT_DOMAIN}`);

    url.protocol = 'https:';
    url.hostname = SHOPIFY_STORE_PERMANENT_DOMAIN;
    url.searchParams.set('channel', 'online_store');
    return url.toString();
  } catch {
    return checkoutUrl;
  }
}

export function isCartNotFoundError(userErrors: Array<{ field: string[] | null; message: string }>): boolean {
  return userErrors.some(e => 
    e.message.toLowerCase().includes('cart not found') || 
    e.message.toLowerCase().includes('does not exist')
  );
}

// Fetch products helper
export async function fetchProducts(first: number = 20, query?: string): Promise<ShopifyProduct[]> {
  const data = await storefrontApiRequest(PRODUCTS_QUERY, { first, query });
  return data?.data?.products?.edges || [];
}

// Fetch single product by handle
export async function fetchProductByHandle(handle: string) {
  const data = await storefrontApiRequest(PRODUCT_BY_HANDLE_QUERY, { handle });
  return data?.data?.productByHandle || null;
}

// ==================== BLOG API ====================

// TypeScript Interfaces for Blog
export interface ShopifyBlogArticle {
  id: string;
  title: string;
  handle: string;
  excerpt: string | null;
  excerptHtml: string | null;
  contentHtml: string;
  content: string;
  publishedAt: string;
  image: {
    url: string;
    altText: string | null;
  } | null;
  authorV2: {
    name: string;
  } | null;
  tags: string[];
  seo: {
    title: string | null;
    description: string | null;
  };
}

export interface ShopifyBlog {
  id: string;
  handle: string;
  title: string;
  articles: {
    edges: Array<{
      node: ShopifyBlogArticle;
    }>;
  };
}

// Query to fetch all blogs and their articles
export const BLOGS_QUERY = `
  query GetBlogs($first: Int!) {
    blogs(first: 10) {
      edges {
        node {
          id
          handle
          title
          articles(first: $first, sortKey: PUBLISHED_AT, reverse: true) {
            edges {
              node {
                id
                title
                handle
                excerpt
                excerptHtml
                contentHtml
                content
                publishedAt
                image {
                  url
                  altText
                }
                authorV2 {
                  name
                }
                tags
                seo {
                  title
                  description
                }
              }
            }
          }
        }
      }
    }
  }
`;

// Query to fetch a single article by blog and article handle
export const ARTICLE_BY_HANDLE_QUERY = `
  query GetArticleByHandle($blogHandle: String!, $articleHandle: String!) {
    blog(handle: $blogHandle) {
      id
      title
      handle
      articleByHandle(handle: $articleHandle) {
        id
        title
        handle
        excerpt
        excerptHtml
        contentHtml
        content
        publishedAt
        image {
          url
          altText
        }
        authorV2 {
          name
        }
        tags
        seo {
          title
          description
        }
      }
    }
  }
`;

export async function fetchBlogArticles(first: number = 50): Promise<ShopifyBlogArticle[]> {
  console.log('[Blog] START fetchBlogArticles with first:', first);
  
  try {
    console.log('[Blog] Calling storefrontApiRequest for blogs...');
    const data = await storefrontApiRequest(BLOGS_QUERY, { first });
    
    console.log('[Blog] API call completed, data:', data);
    
    if (!data) {
      console.warn('[Blog] API returned null (possibly 402 Payment Required)');
      return [];
    }
    
    if (!data?.data?.blogs?.edges) {
      console.warn('[Blog] No blogs found in response:', data);
      return [];
    }
    
    // Flatten all articles from all blogs
    const allArticles: ShopifyBlogArticle[] = [];
    for (const blogEdge of data.data.blogs.edges) {
      const blog = blogEdge.node;
      console.log(`[Blog] Found blog: ${blog.title} with ${blog.articles.edges.length} articles`);
      for (const articleEdge of blog.articles.edges) {
        allArticles.push(articleEdge.node);
      }
    }
    
    // Sort by publishedAt descending
    allArticles.sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
    
    console.log(`[Blog] Total articles fetched: ${allArticles.length}`);
    
    return allArticles;
  } catch (error) {
    console.error('[Blog] ERROR in fetchBlogArticles:', error);
    throw error;
  }
}

// Fetch a single article by handle (tries all blogs)
export async function fetchArticleByHandle(articleHandle: string): Promise<{ article: ShopifyBlogArticle | null; blogHandle: string | null }> {
  // First, get all blogs to find which one contains the article
  const blogsData = await storefrontApiRequest(BLOGS_QUERY, { first: 100 });
  if (!blogsData?.data?.blogs?.edges) return { article: null, blogHandle: null };
  
  // Search through all blogs for the article
  for (const blogEdge of blogsData.data.blogs.edges) {
    const blog = blogEdge.node;
    for (const articleEdge of blog.articles.edges) {
      if (articleEdge.node.handle === articleHandle) {
        return { article: articleEdge.node, blogHandle: blog.handle };
      }
    }
  }
  
  return { article: null, blogHandle: null };
}

// ==================== COLLECTION API ====================

// TypeScript Interfaces for Collections
export interface ShopifyCollection {
  id: string;
  handle: string;
  title: string;
  description: string;
  descriptionHtml: string;
  image: {
    url: string;
    altText: string | null;
  } | null;
  seo: {
    title: string | null;
    description: string | null;
  };
}

// Query to fetch all collections with SEO data
export const COLLECTIONS_QUERY = `
  query GetCollections($first: Int!) {
    collections(first: $first) {
      edges {
        node {
          id
          handle
          title
          description
          descriptionHtml
          image {
            url
            altText
          }
          seo {
            title
            description
          }
        }
      }
    }
  }
`;

// Query to fetch a single collection by handle with SEO data
export const COLLECTION_BY_HANDLE_QUERY = `
  query GetCollectionByHandle($handle: String!) {
    collection(handle: $handle) {
      id
      handle
      title
      description
      descriptionHtml
      image {
        url
        altText
      }
      seo {
        title
        description
      }
    }
  }
`;

// Fetch all collections
export async function fetchCollections(first: number = 50): Promise<ShopifyCollection[]> {
  const data = await storefrontApiRequest(COLLECTIONS_QUERY, { first });
  if (!data?.data?.collections?.edges) return [];
  return data.data.collections.edges.map((edge: { node: ShopifyCollection }) => edge.node);
}

// Fetch a single collection by handle with SEO data
export async function fetchCollectionByHandle(handle: string): Promise<ShopifyCollection | null> {
  const data = await storefrontApiRequest(COLLECTION_BY_HANDLE_QUERY, { handle });
  return data?.data?.collection || null;
}